// GET /v1/apps/:appId/knowledge-doc, POST /v1/apps/:appId/ask-crew
// (packages/shared/src/http/knowledge.ts:31,39; docs/design/slice3-knowledge-ux.md §4/§5).
// Every response parses through the SAME shared Zod schema apps/api validates against
// (app-engineering §2 "parse, don't validate"; the Slice-2 wire-drift lesson,
// docs/memory/04-active-context.md) - never a re-derived client-side shape.
import { AskCrewRequest, AskCrewResponse, KnowledgeDocResponse } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import { isBoundaryCode, type ClientError } from "./errors.js";

export const getKnowledgeDoc = (
  appId: AppId,
): Promise<Result<KnowledgeDocResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/knowledge-doc`, KnowledgeDocResponse);

export const askCrew = (
  appId: AppId,
  question: AskCrewRequest,
): Promise<Result<AskCrewResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/ask-crew`, AskCrewResponse, question);

/**
 * `KnowledgeErrorCode` is `unavailable | not_found | forbidden | invalid_query`
 * (packages/shared/src/ports/knowledge.ts:139). A `not_found` and a `forbidden` on a
 * cross-tenant app are byte-identical by design (http/knowledge.ts:17-19, ux spec §2.3) -
 * but that ambiguity is resolved one layer up, in `AppDetail` (src/screens/AppDetail.tsx),
 * whose own `getApp` ownership check gates BOTH knowledge screens before either ever
 * mounts. So a `not_found`/`forbidden` reaching a knowledge call AFTER that gate has
 * already passed can only mean "this app's doc/chat state" (not built yet, in this
 * account), never "not yours" again - the two codes collapse to one UI meaning here.
 * `validation` is included defensively: the HTTP-layer comment (http/knowledge.ts:16)
 * documents that alias for the same rejection `invalid_query` names at the seam level;
 * ASSUMED, not confirmed against a live apps/api response (that lane is in flight this
 * same wave) - flagged in the dispatch return.
 */
export type KnowledgeErrorKind =
  | "not_found"
  | "unavailable"
  | "invalid_query"
  | "building"
  | "audit_failed"
  | "unknown";

export const knowledgeErrorKind = (error: ClientError): KnowledgeErrorKind => {
  // Doc-lifecycle codes CONFIRMED on the wire (apps/api knowledge.service.ts emits the
  // free-string codes "building" and "audit_failed" under 503 - the shared
  // KnowledgeErrorCode enum lacks them, flagged to the port owner by both lanes; the
  // ErrorEnvelope.code is a free string, so this is wire-legal). Wave-3 seam
  // reconciliation, 2026-07-24 - replaces the interim client-side heuristics.
  if (isBoundaryCode(error, "building")) return "building";
  if (isBoundaryCode(error, "audit_failed")) return "audit_failed";
  if (isBoundaryCode(error, "not_found") || isBoundaryCode(error, "forbidden")) return "not_found";
  if (isBoundaryCode(error, "unavailable")) return "unavailable";
  if (isBoundaryCode(error, "invalid_query") || isBoundaryCode(error, "validation"))
    return "invalid_query";
  return "unknown";
};

/**
 * The throttle state (ux spec §5.3 "Rate-limited/refused - throttle"). No dedicated
 * knowledge-specific code is confirmed on the wire yet (ux spec §6.1 item 6) - this
 * checks the GENERAL closed boundary code set's own `rate_limited` value
 * (packages/shared/src/result.ts:47 ERROR_CODES), not an invented string, since that is
 * the one already-real code this shape of refusal would use if apps/api emits it
 * unchanged. ASSUMED wire behavior - flagged in the dispatch return.
 */
export const isThrottled = (error: ClientError): boolean => isBoundaryCode(error, "rate_limited");
